// src/components/AIChat.js - Design Moderne + Logique API

import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next'; 
import { FaPaperPlane, FaRobot, FaUser, FaSpinner } from 'react-icons/fa';

const API_BASE_URL = 'https://api.blackroq.pro/api/ai';

const AIChat = () => {
    const { t } = useTranslation(); 
    const chatContainerRef = useRef(null);
    const [prompt, setPrompt] = useState('');
    const [messages, setMessages] = useState([]); 
    const [loading, setLoading] = useState(false);
    const [historyLoaded, setHistoryLoaded] = useState(false); 

    const token = localStorage.getItem('token'); 

    // Défilement automatique vers le bas lors de l'ajout d'un message
    useEffect(() => {
        if (chatContainerRef.current) {
            chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
        }
    }, [messages, loading]); // Utiliser 'loading' pour l'indicateur de frappe

    // LOGIQUE: Chargement de l'historique
    const fetchHistory = useCallback(async () => {
        if (!token) {
            setMessages([{ sender: 'ai', text: t('chatUnauthenticatedMsg') }]);
            setHistoryLoaded(true);
            return;
        }
        
        try {
            const config = { 
                headers: { 
                    'x-auth-token': token, 
                    'Content-Type': 'application/json' 
                } 
            };
            const response = await axios.get(`${API_BASE_URL}/history`, config);
            
            // Si l'historique est vide, afficher un message de bienvenue
            if (response.data.length === 0) {
                 setMessages([{ sender: 'ai', text: t('chatWelcomeMessage') }]);
            } else {
                 setMessages(response.data);
            }
           
            setHistoryLoaded(true);
            
        } catch (error) {
            console.error('Erreur lors du chargement de l\'historique:', error);
            // Afficher le message d'erreur de chargement
            setMessages([{ sender: 'ai', text: t('chatErrorLoadHistory') }]);
            setHistoryLoaded(true); 
        }
    }, [token, t]);

    useEffect(() => {
        fetchHistory();
    }, [fetchHistory]); 
    
    // LOGIQUE: Envoi du message
    const handleSend = async (e) => {
        e.preventDefault();
        if (!prompt.trim() || loading || !token) return;

        const currentPrompt = prompt;
        const userMessage = { sender: 'user', text: currentPrompt };
        setMessages(prev => [...prev, userMessage]);
        setPrompt('');
        setLoading(true);

        try {
            const config = { 
                headers: { 
                    'x-auth-token': token, 
                    'Content-Type': 'application/json' 
                } 
            };
            
            const response = await axios.post(
                `${API_BASE_URL}/chat`, 
                { prompt: currentPrompt }, 
                config
            );

            const aiMessage = { sender: 'ai', text: response.data.aiResponse };
            setMessages(prev => [...prev, aiMessage]);

        } catch (error) {
            console.error('Erreur lors de l\'envoi du message:', error);
            const errorResponseData = error.response?.data;
            
            const defaultError = t('chatApiError');
            
            const errorMessage = errorResponseData && (errorResponseData.aiResponse || errorResponseData.message)
                ? (errorResponseData.aiResponse || errorResponseData.message)
                : defaultError;
            
            const errorMsg = { sender: 'ai', text: errorMessage };
            setMessages(prev => [...prev, errorMsg]);
        } finally {
            setLoading(false);
        }
    };
    
    // Composant pour afficher un message avec le style de bulles
    const ChatMessage = ({ msg }) => {
        const isUser = msg.sender === 'user';
        const icon = isUser ? <FaUser className="ms-2 mt-1" style={{ minWidth: '18px' }} /> : <FaRobot className="me-2 mt-1" style={{ minWidth: '18px' }} />;
        
        return (
            <div className={`d-flex ${isUser ? 'justify-content-end' : 'justify-content-start'} mb-3`}>
                <div 
                    className={`card p-3 shadow-sm border-0 ${isUser ? 'bg-primary text-white' : 'bg-light text-dark'}`}
                    style={{ 
                        maxWidth: '85%', 
                        borderRadius: '1rem',
                        borderBottomRightRadius: isUser ? '0.3rem' : '1rem',
                        borderBottomLeftRadius: isUser ? '1rem' : '0.3rem',
                    }}
                >
                    <div className="d-flex align-items-start">
                        {!isUser && icon}
                        <p className="mb-0" style={{ whiteSpace: 'pre-wrap' }}>
                            {msg.text}
                        </p>
                        {isUser && icon}
                    </div>
                </div>
            </div>
        );
    };


    // Rendu Principal
    if (!historyLoaded) {
        return (
            <div className="card shadow-lg p-5 text-center">
                 <FaSpinner className="fa-spin me-2 fs-3 text-primary mx-auto mb-3" />
                 <p className="mb-0">{t('chatLoading')}</p>
            </div>
        );
    }

    return (
        <div className="card shadow-lg h-100 d-flex flex-column">
            
            {/* En-tête du Chat (Rappel: le titre complet est dans le parent GeminiChat.js) */}
            <div className="card-header bg-white border-bottom p-3">
                <h4 className="mb-0 fw-bold">
                    <FaRobot className="me-2 text-primary" />
                    {t('ai_assistant_title')}
                </h4>
            </div>
            
            {/* Corps du Chat (Messages) */}
            <div 
                ref={chatContainerRef} 
                className="card-body p-4 flex-grow-1" 
                style={{ overflowY: 'auto', maxHeight: '70vh', minHeight: '300px' }} 
            >
                
                {messages.map((msg, index) => (
                    // L'historique chargé affiche toujours le message AI (bienvenue ou erreur) comme premier élément
                    // Si l'historique est chargé, on affiche les messages
                    <ChatMessage key={index} msg={msg} />
                ))}

                {/* Indicateur de Frappe de l'IA */}
                {loading && (
                    <div className="d-flex justify-content-start mb-3">
                        <div className="card p-3 bg-light text-muted border-0 shadow-sm" style={{ maxWidth: '70%', borderRadius: '1rem', borderBottomLeftRadius: '0.3rem' }}>
                            <FaSpinner className="fa-spin me-2" />
                            {t('chat_typing_indicator')}
                        </div>
                    </div>
                )}
            </div>
            
            {/* Barre de Saisie (Fixe) */}
            <div className="card-footer bg-light border-top p-3">
                {!token ? (
                    <p className="text-danger mb-0">{t('chatUnauthenticatedMsg')}</p>
                ) : (
                    <form onSubmit={handleSend} className="d-flex">
                        <input
                            type="text"
                            className="form-control me-3 p-3"
                            placeholder={t('chat_placeholder')}
                            value={prompt}
                            onChange={(e) => setPrompt(e.target.value)}
                            disabled={loading}
                        />
                        <button
                            type="submit"
                            className="btn btn-primary px-4"
                            disabled={!prompt.trim() || loading}
                        >
                            <FaPaperPlane className="me-2" />
                            {t('chat_send_button')}
                        </button>
                    </form>
                )}
            </div>

        </div>
    );
};

export default AIChat;